// ═══════════════════════════════════════════════════════════════════════════
// PAGE: RÉUNIONS & NOTES
// ═══════════════════════════════════════════════════════════════════════════
//
// L'édition / création d'une réunion vit dans page-meeting.ts depuis le
// refacto RefactoUX/Réunions (mai 2026, suite au commit 6d9abc1 qui a fait
// la même bascule popups→pages pour actions / projet / programme).
//
// Affichage (changement juin 2026) :
//   - Liste ET calendrier sont bornés au MOIS affiché : la lecture en BD ne
//     porte que sur les réunions du mois (date_from/date_to) ; changer de mois
//     déclenche un nouveau SELECT (fetchMonth).
//   - Toutes les réunions du tenant sont visibles (plus de filtre par
//     participation côté API — cf. list_meetings).
//   - Calendrier colorié par TYPE DE COMITÉ (committeeColor).
//   - Filtre comité = multi-select (tous cochés à l'arrivée).
//   - Filtres projet / structure alignés sur la page #actions (Tous / Aucun).

import { navigateTo } from '../router.js';
import { filterOptions, renderMeetingRows, type ProgramRef } from './page-meetings-records-rows.js';
import { monthLabel, renderCalendarGrid } from './page-meetings-records-calendar.js';
import {
  applyStickyOffsets,
  renderGuidesGrid,
  type GuidesOverview,
} from './page-meetings-records-guides.js';
import type { GridSortKey } from '../domain/meeting-guides-grid.js';
import { sortMeetings, type SortKey } from '../domain/meeting-list-sort.js';

export interface MeetingRecord {
  id: number;
  project_id: number | null;
  project_code: string | null;
  project_name: string | null;
  program_id: number | null;
  committee_id: number;
  committee_code: string | null;
  committee_name: string | null;
  structure_id: number | null;
  structure_code: string | null;
  structure_label: string | null;
  meeting_date: string | null;
  meeting_time: string | null;
  label: string;
  with_risks: boolean;
  created_by: number | null;
  opened_at: string | null;
  closed_at: string | null;
  participants: MeetingUser[];
  animators: MeetingUser[];
  cr_count: number;
  notes_count: number;
  // Réunion issue d'un calendrier utilisateur (ical_uid présent) : picto + garde de déplacement.
  from_calendar?: boolean;
}

interface ProgramItem {
  id: number;
  code?: string | null;
  name: string;
}

export interface MeetingUser {
  id: number;
  trigram: string;
  name: string;
}

interface CommitteeItem {
  id: number;
  code: string;
  name: string;
}

interface ProjectItem {
  id: number;
  code: string;
  name: string;
}

interface StructureItem {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

pages.meetingsRecords = async function () {
  setHeader(
    t('pageMeetingsRecords.title'),
    `<button class="btn btn-primary ml-auto" id="btn-mr-add">${esc(t('pageMeetingsRecords.btnAdd'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  try {
    // Référentiels chargés une seule fois ; les réunions sont chargées par
    // mois (fetchMonth) et re-chargées au changement de mois.
    const [committees, projects, programs, structures] = await Promise.all([
      api.getCommittees() as Promise<CommitteeItem[]>,
      api.getProjects() as Promise<ProjectItem[]>,
      api.getPrograms() as Promise<ProgramItem[]>,
      (api.getStructuresIfAllowed() as Promise<StructureItem[]>).catch(() => []),
    ]);
    renderMeetingsPage(committees, projects, programs, structures);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderMeetingsPage(
  committees: CommitteeItem[],
  projects: ProjectItem[],
  programs: ProgramItem[],
  structures: StructureItem[],
): void {
  // Index programme par id pour rendre la colonne "Prog — Proj" (code affiché,
  // libellé en infobulle) sans requête supplémentaire par ligne.
  const programById = new Map<number, ProgramRef>(
    programs.map((p) => [p.id, { code: p.code || '', name: p.name || '' }]),
  );
  // Tri par défaut : Date décroissante (du plus récent au plus ancien) —
  // c'est l'info la plus utile au scan.
  let sortKey: SortKey = 'meeting_date';
  let sortDir: 'asc' | 'desc' = 'desc';

  // Réunions du mois courant (rechargées à chaque changement de mois).
  let meetings: MeetingRecord[] = [];

  // Restauration des paramètres d'affichage (filtres + mode + mois) tels que
  // laissés au dernier passage sur la page — stockage isolé par tenant.
  const saved = _loadViewState();
  const _today = new Date();
  // Filtres projet / structure : 'all' | 'none' | '<id>' (parité page #actions).
  let filterProject = _coerceFilter(saved.filterProject);
  let filterStructure = _coerceFilter(saved.filterStructure);
  // Filtre comité : select simple 'all' | '<id>' (même style UI que Structure).
  let filterCommittee = _coerceFilter(saved.filterCommittee);
  let view: MrView = _coerceView(saved.view);
  // Vue « Guides » : matrice réunions × guides du référentiel, chargée
  // avec le mois (une seule lecture pour tout le tableau).
  let overview: GuidesOverview = { definitions: [], meetings: {} };
  let guideSortKey: GridSortKey = 'meeting_date';
  let guideSortDir: 'asc' | 'desc' = 'asc';
  let calYear = saved.calYear ?? _today.getFullYear();
  let calMonth = saved.calMonth ?? _today.getMonth(); // 0-based

  const persist = (): void =>
    _saveViewState({ view, filterProject, filterStructure, filterCommittee, calYear, calMonth });

  const canWrite = !!(window.hasPermission && window.hasPermission('meetings_records.write'));

  // Le paramètre union évite que TS ne « narrow » `view` au littéral courant.
  const viewActive = (v: MrView): string => (view === v ? ' active' : '');

  const monthLabelText = (): string => monthLabel(calYear, calMonth);

  // Les réunions sont déjà bornées au mois côté serveur ; ici on n'applique
  // que les filtres projet / structure / comité (et le tri).
  const getFiltered = (): MeetingRecord[] => {
    let rows = meetings;
    if (filterProject === 'none') rows = rows.filter((m) => !m.project_id);
    else if (filterProject !== 'all') {
      const pid = parseInt(filterProject);
      rows = rows.filter((m) => m.project_id === pid);
    }
    if (filterStructure === 'none') rows = rows.filter((m) => !m.structure_id);
    else if (filterStructure !== 'all') {
      const sid = parseInt(filterStructure);
      rows = rows.filter((m) => m.structure_id === sid);
    }
    if (filterCommittee !== 'all') {
      const cid = parseInt(filterCommittee);
      rows = rows.filter((m) => m.committee_id === cid);
    }
    return sortMeetings(rows, sortKey, sortDir);
  };

  const th = (key: SortKey, label: string, extra = ''): string => {
    const active = sortKey === key;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return `<th class="cursor-pointer${active ? ' sorted' : ''}" data-sort-mr="${key}"${extra ? ` style="${extra}"` : ''}>${esc(label)} <span class="text-xs">${arrow}</span></th>`;
  };

  // Rendu des lignes de la vue liste : délégué au module frère (pur, immuables
  // `programById`/`canWrite` threadés).
  const renderRows = (rows: MeetingRecord[]): string =>
    renderMeetingRows(rows, programById, canWrite);

  // ── Vue LISTE (table) ────────────────────────────────────────────────────
  // Bornée au mois affiché (mêmes réunions que le calendrier).
  const renderListView = (): string => `
    <div class="card">
      <div class="table-wrap">
        <table class="table" id="mr-table">
          <thead>
            <tr>
              ${th('id', t('pageMeetingsRecords.colNum'), 'width:48px')}
              ${th('label', t('pageMeetingsRecords.colLabel'))}
              ${th('project_code', t('pageMeetingsRecords.colProgramProject'))}
              ${th('committee_code', t('pageMeetingsRecords.colCommittee'))}
              ${th('meeting_date', t('pageMeetingsRecords.colDate'))}
              ${th('animators_count', t('pageMeetingsRecords.colAnimators'))}
              ${th('participants_count', t('pageMeetingsRecords.colParticipants'))}
              ${th('notes_count', t('pageMeetingsRecords.colNotes'), 'width:60px;text-align:center')}
              <th>${esc(t('pageMeetingsRecords.colActions'))}</th>
            </tr>
          </thead>
          <tbody id="mr-tbody">
            ${renderRows(getFiltered())}
          </tbody>
        </table>
      </div>
    </div>`;

  // ── Vue CALENDRIER (mois) ────────────────────────────────────────────────
  // Grille déléguée au module frère (rendu pur) ; la navigation de mois vit
  // dans la barre d'outils, commune aux trois vues.
  const renderCalendarView = (): string =>
    renderCalendarGrid(getFiltered(), calYear, calMonth, _today, canWrite);

  // ── Options des filtres ──────────────────────────────────────────────────
  const projectOpts = filterOptions(
    projects.map((p) => ({ id: p.id, code: p.code, label: p.name })),
    filterProject,
  );
  const structureOpts = filterOptions(
    structures
      .slice()
      .filter((s) => s.is_active !== false)
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((s) => ({ id: s.id, code: s.code, label: s.label })),
    filterStructure,
  );
  const committeeOpts = filterOptions(
    committees
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .map((c) => ({ id: c.id, code: c.code, label: c.name || '' })),
    filterCommittee,
  );

  // Filtres (projet / comité / structure, tous en selects Tous + liste) puis,
  // EN DESSOUS, le bloc nav mois (◀ mois ▶ Aujourd'hui). Bascule vue à droite.
  const html = `
    <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px">
      <label style="display:flex;align-items:center;gap:6px;font-size:13px">
        ${esc(t('pageMeetingsRecords.filterProject'))}
        <select id="mr-filter-project" class="form-control" style="min-width:130px">
          <option value="all"${filterProject === 'all' ? ' selected' : ''}>${esc(t('pageMeetingsRecords.filterAll'))}</option>
          <option value="none"${filterProject === 'none' ? ' selected' : ''}>${esc(t('pageMeetingsRecords.filterProjectNone'))}</option>
          ${projectOpts}
        </select>
      </label>
      <label style="display:flex;align-items:center;gap:6px;font-size:13px">
        ${esc(t('pageMeetingsRecords.filterCommittee'))}
        <select id="mr-filter-committee" class="form-control" style="min-width:130px">
          <option value="all"${filterCommittee === 'all' ? ' selected' : ''}>${esc(t('pageMeetingsRecords.filterAll'))}</option>
          ${committeeOpts}
        </select>
      </label>
      <label style="display:flex;align-items:center;gap:6px;font-size:13px">
        ${esc(t('pageMeetingsRecords.filterStructure'))}
        <select id="mr-filter-structure" class="form-control" style="min-width:130px">
          <option value="all"${filterStructure === 'all' ? ' selected' : ''}>${esc(t('pageMeetingsRecords.filterAll'))}</option>
          <option value="none"${filterStructure === 'none' ? ' selected' : ''}>${esc(t('pageMeetingsRecords.filterStructureNone'))}</option>
          ${structureOpts}
        </select>
      </label>
      <div class="mr-view-toggle" style="margin-left:auto">
        <button class="mr-view-btn${viewActive('list')}" data-mr-view="list">${esc(t('pageMeetingsRecords.viewList'))}</button>
        <button class="mr-view-btn${viewActive('calendar')}" data-mr-view="calendar">${esc(t('pageMeetingsRecords.viewCalendar'))}</button>
        <button class="mr-view-btn${viewActive('guides')}" data-mr-view="guides">${esc(t('pageMeetingsRecords.viewGuides'))}</button>
      </div>
    </div>
    <div class="cal-header" style="display:flex;align-items:center;gap:6px;margin-bottom:12px">
      <button class="btn btn-secondary btn-icon" id="mr-month-prev" aria-label="${esc(t('pageMeetingsRecords.calPrev'))}">◀</button>
      <span class="cal-month" id="mr-month-label" style="min-width:140px;text-align:center">${esc(monthLabelText())}</span>
      <button class="btn btn-secondary btn-icon" id="mr-month-next" aria-label="${esc(t('pageMeetingsRecords.calNext'))}">▶</button>
      <button class="btn btn-secondary" id="mr-month-today">${esc(t('pageMeetingsRecords.calToday'))}</button>
    </div>
    <div id="mr-view"></div>`;

  setContent(html);

  const viewEl = document.getElementById('mr-view');
  if (!viewEl) return;
  // ── Vue GUIDES (matrice) ─────────────────────────────────────────────────
  // Ne montre que les réunions ANIMÉES par l'utilisateur : la matrice renvoyée
  // par l'API est déjà bornée à celles-là, on ne garde que les lignes qui y
  // figurent. Les filtres projet / comité / structure s'appliquent ensuite,
  // comme aux deux autres vues.
  const renderGuidesView = (): string =>
    renderGuidesGrid(
      getFiltered().filter((m) => overview.meetings[String(m.id)] !== undefined),
      overview,
      programById,
      guideSortKey,
      guideSortDir,
    );

  const renderView = (): void => {
    viewEl.innerHTML =
      view === 'list'
        ? renderListView()
        : view === 'calendar'
          ? renderCalendarView()
          : renderGuidesView();
    // Les colonnes figées se mesurent une fois le tableau dans le DOM.
    if (view === 'guides') applyStickyOffsets(viewEl);
  };

  // Sans ce recalcul, redimensionner décale le bloc figé et laisse le contenu
  // qui défile transparaître. `ResizeObserver` et non `resize` : il capte aussi
  // le volet latéral et le zoom, qui n'émettent pas `resize`.
  new ResizeObserver(() => {
    if (view === 'guides') applyStickyOffsets(viewEl);
  }).observe(viewEl);

  // ── Chargement du mois (nouveau SELECT borné date_from/date_to) ──────────
  const fetchMonth = async (): Promise<void> => {
    const { from, to } = _monthRange(calYear, calMonth);
    viewEl.innerHTML = `<div class="loading">${esc(t('common.loading'))}</div>`;
    try {
      // La matrice des guides suit le même mois que les réunions : une
      // lecture de plus, en parallèle, plutôt qu'un appel par ligne au rendu.
      const [rows, ov] = await Promise.all([
        api.getMeetingsRecords({ date_from: from, date_to: to }) as Promise<MeetingRecord[]>,
        api.getGuidesOverview({ date_from: from, date_to: to }) as Promise<GuidesOverview>,
      ]);
      meetings = rows;
      overview = ov;
    } catch (err: unknown) {
      meetings = [];
      overview = { definitions: [], meetings: {} };
      viewEl.innerHTML = `<div class="error">${esc(err instanceof Error ? err.message : t('common.error'))}</div>`;
      return;
    }
    renderView();
  };

  // ── Actions réunion (mutualisées liste + calendrier) ─────────────────────
  const deleteMeetingById = (id: number): void => {
    if (!confirm(t('pageMeetingsRecords.confirmDelete'))) return;
    void (async () => {
      try {
        await api.deleteMeetingRecord(id);
        const idx = meetings.findIndex((x) => x.id === id);
        if (idx >= 0) meetings.splice(idx, 1);
        renderView();
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    })();
  };

  // Dupliquer : ouvre la création pré-remplie (libellé, prog/projet/structure/
  // comité, flag risques, participants, animateurs). Date & reste par défaut.
  const duplicateMeeting = (id: number): void => {
    const m = meetings.find((x) => x.id === id);
    if (!m) return;
    _setCreatePrefill({
      label: m.label,
      programId: m.program_id ?? 0,
      projectId: m.project_id ?? 0,
      structureId: m.structure_id ?? 0,
      committeeId: m.committee_id,
      withRisks: m.with_risks,
      participantIds: m.participants.map((p) => p.id),
      animatorIds: m.animators.map((a) => a.id),
    });
    navigateTo('meeting/:id', { id: 0 });
  };

  // Ajouter depuis une case : création avec la date pré-remplie.
  const addOnDate = (dateKey: string): void => {
    _setCreatePrefill({ meetingDate: dateKey });
    navigateTo('meeting/:id', { id: 0 });
  };

  // Glisser-déposer d'une réunion : seule la date change (PUT complet, le
  // backend n'expose pas de patch partiel — on rejoue le reste à l'identique).
  // On re-fetch ensuite le mois : si la réunion a été déplacée hors du mois
  // affiché (case grisée d'un mois adjacent), elle doit disparaître de la vue.
  const moveMeeting = async (id: number, newDate: string): Promise<void> => {
    const m = meetings.find((x) => x.id === id);
    if (!m || !newDate || m.meeting_date === newDate) return;
    // Réunion issue d'un calendrier : le déplacement reste local à Ornexia (on ne
    // republie rien vers l'agenda d'origine) → confirmation explicite. En cas de
    // refus, rien n'a bougé dans le DOM (le drop HTML5 ne déplace pas l'élément de
    // lui-même), donc pas besoin de re-rendre.
    if (m.from_calendar) {
      const ok = await confirmStackModal({
        message: t('pageMeetingsRecords.moveCalendarConfirm'),
        confirmLabel: t('pageMeetingsRecords.moveCalendarBtn'),
      });
      if (!ok) return;
    }
    try {
      await api.updateMeetingRecord(id, {
        project_id: m.project_id,
        program_id: m.program_id,
        structure_id: m.structure_id,
        committee_id: m.committee_id,
        meeting_date: newDate,
        meeting_time: m.meeting_time,
        label: m.label,
        with_risks: m.with_risks,
        participant_ids: m.participants.map((p) => p.id),
        animator_ids: m.animators.map((a) => a.id),
      });
      await fetchMonth();
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : t('common.error'), 'error');
    }
  };

  // ── Câblage filtres ──────────────────────────────────────────────────────
  document.getElementById('mr-filter-project')?.addEventListener('change', (e) => {
    filterProject = (e.target as HTMLSelectElement).value || 'all';
    persist();
    renderView();
  });
  document.getElementById('mr-filter-structure')?.addEventListener('change', (e) => {
    filterStructure = (e.target as HTMLSelectElement).value || 'all';
    persist();
    renderView();
  });
  document.getElementById('mr-filter-committee')?.addEventListener('change', (e) => {
    filterCommittee = (e.target as HTMLSelectElement).value || 'all';
    persist();
    renderView();
  });

  // ── Navigation de mois (commune liste + calendrier) ──────────────────────
  const navMonth = async (delta: number): Promise<void> => {
    calMonth += delta;
    if (calMonth < 0) {
      calMonth = 11;
      calYear -= 1;
    } else if (calMonth > 11) {
      calMonth = 0;
      calYear += 1;
    }
    persist();
    const lbl = document.getElementById('mr-month-label');
    if (lbl) lbl.textContent = monthLabelText();
    await fetchMonth();
  };
  document.getElementById('mr-month-prev')?.addEventListener('click', () => void navMonth(-1));
  document.getElementById('mr-month-next')?.addEventListener('click', () => void navMonth(1));
  // Ramène le calendrier au mois courant (date du jour).
  const goToday = async (): Promise<void> => {
    calYear = _today.getFullYear();
    calMonth = _today.getMonth();
    persist();
    const lbl = document.getElementById('mr-month-label');
    if (lbl) lbl.textContent = monthLabelText();
    await fetchMonth();
  };
  document.getElementById('mr-month-today')?.addEventListener('click', () => void goToday());

  // ── Bascule liste / calendrier ───────────────────────────────────────────
  document.querySelectorAll<HTMLElement>('.mr-view-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = _coerceView(btn.dataset['mrView']);
      if (next === view) return;
      view = next;
      document.querySelectorAll('.mr-view-btn').forEach((b) => {
        b.classList.toggle('active', (b as HTMLElement).dataset['mrView'] === view);
      });
      persist();
      renderView();
    });
  });

  if (canWrite) {
    document.getElementById('btn-mr-add')?.addEventListener('click', () => {
      navigateTo('meeting/:id', { id: 0 });
    });
  } else {
    const addBtn = document.getElementById('btn-mr-add');
    if (addBtn) addBtn.style.display = 'none';
  }

  // Délégation unique sur le conteneur de vue (re-rendu à chaque changement).
  viewEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Ajouter une réunion sur une case (vue calendrier)
    const addBtn = target.closest<HTMLElement>('[data-cal-add]');
    if (addBtn) {
      addOnDate(addBtn.dataset['calAdd'] || '');
      return;
    }

    // Dupliquer une réunion (vue calendrier)
    const dupBtn = target.closest<HTMLElement>('[data-cal-dup]');
    if (dupBtn) {
      const id = parseInt(dupBtn.dataset['calDup'] || '0');
      if (id) duplicateMeeting(id);
      return;
    }

    // Supprimer une réunion (vue calendrier)
    const calDel = target.closest<HTMLElement>('[data-cal-del]');
    if (calDel) {
      const id = parseInt(calDel.dataset['calDel'] || '0');
      if (id) deleteMeetingById(id);
      return;
    }

    // Clic sur une réunion (vue calendrier) → édition
    const calMeet = target.closest<HTMLElement>('[data-cal-meeting]');
    if (calMeet) {
      const id = parseInt(calMeet.dataset['calMeeting'] || '0');
      if (id) navigateTo('meeting/:id', { id });
      return;
    }

    // Tri (vue guides) — clé propre, les colonnes n'étant pas les mêmes.
    const thGuide = target.closest<HTMLElement>('[data-sort-mrg]');
    if (thGuide) {
      const key = thGuide.dataset['sortMrg'] as GridSortKey;
      if (guideSortKey === key) {
        guideSortDir = guideSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        guideSortKey = key;
        guideSortDir = 'asc';
      }
      renderView();
      return;
    }

    // Tri (vue liste)
    const thEl = target.closest<HTMLElement>('[data-sort-mr]');
    if (thEl) {
      const key = thEl.dataset['sortMr'] as SortKey;
      if (sortKey === key) {
        sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey = key;
        sortDir = 'asc';
      }
      renderView();
      return;
    }

    // Navigation réunion (vue liste)
    const editCell = target.closest<HTMLElement>('[data-mr-edit]');
    if (editCell && !target.closest('[data-mr-btn]') && !target.closest('.mr-btn-delete')) {
      const id = parseInt(editCell.dataset['mrEdit'] || '0');
      if (id) navigateTo('meeting/:id', { id });
      return;
    }

    const editBtn = target.closest<HTMLElement>('.mr-btn-edit');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['id'] || '0');
      if (id) navigateTo('meeting/:id', { id });
      return;
    }

    const deleteBtn = target.closest<HTMLElement>('.mr-btn-delete');
    if (deleteBtn) {
      const id = parseInt(deleteBtn.dataset['id'] || '0');
      deleteMeetingById(id);
    }
  });

  // ── Glisser-déposer (vue calendrier) : déplace la date d'une réunion ──────
  let dragMeetingId = 0;
  viewEl.addEventListener('dragstart', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('.cal-meet');
    if (!el) return;
    dragMeetingId = parseInt(el.dataset['calMeeting'] || '0');
    el.classList.add('cal-dragging');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(dragMeetingId));
    }
  });
  viewEl.addEventListener('dragend', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('.cal-meet');
    if (el) el.classList.remove('cal-dragging');
    viewEl.querySelectorAll('.cal-drop').forEach((c) => c.classList.remove('cal-drop'));
    dragMeetingId = 0;
  });
  viewEl.addEventListener('dragover', (e) => {
    if (!dragMeetingId) return;
    const cell = (e.target as HTMLElement).closest<HTMLElement>('[data-cal-date]');
    if (!cell) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    viewEl.querySelectorAll('.cal-drop').forEach((c) => c.classList.remove('cal-drop'));
    cell.classList.add('cal-drop');
  });
  viewEl.addEventListener('drop', (e) => {
    const cell = (e.target as HTMLElement).closest<HTMLElement>('[data-cal-date]');
    if (!cell) return;
    e.preventDefault();
    const id = dragMeetingId || parseInt(e.dataTransfer?.getData('text/plain') || '0');
    const newDate = cell.dataset['calDate'] || '';
    cell.classList.remove('cal-drop');
    if (id && newDate) void moveMeeting(id, newDate);
  });

  // Chargement initial du mois courant (puis rendu de la vue active).
  void fetchMonth();
}

// ── Helpers locaux ────────────────────────────────────────────────────────────

// Normalise un filtre persisté (ancien format numérique 0=Tous → 'all').
function _coerceFilter(v: unknown): string {
  if (v === null || v === undefined) return 'all';
  if (typeof v === 'number') return v === 0 ? 'all' : String(v);
  const s = String(v);
  return s === '0' || s === '' ? 'all' : s;
}

/** Modes d'affichage de la page. */
export type MrView = 'list' | 'calendar' | 'guides';

/** Valide un mode venu du stockage : une vue inconnue (ancienne version, clé
 *  bricolée) retombe sur la liste plutôt que de casser l'affichage. Le mode
 *  `controls` d'avant le renommage d'août 2026 retombe donc sur la liste — un
 *  clic suffit à retrouver la matrice, ça ne justifie pas de garder l'alias. */
function _coerceView(v: string | undefined): MrView {
  return v === 'calendar' || v === 'guides' ? v : 'list';
}

// Plage de dates inclusive (YYYY-MM-DD) du mois affiché (month 0-based).
function _monthRange(year: number, month: number): { from: string; to: string } {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const from = `${year}-${pad(month + 1)}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const to = `${year}-${pad(month + 1)}-${pad(lastDay)}`;
  return { from, to };
}

// Persistance des paramètres d'affichage (filtres + mode + mois), isolée par
// tenant via les wrappers tls* (préfixe hostname). Le filtre comité n'est PAS
// persisté (tous cochés à chaque arrivée, par décision UX).
const MR_VIEW_STATE_KEY = 'meetingsRecords:viewState';
interface MrViewState {
  view: MrView;
  filterProject: string;
  filterStructure: string;
  filterCommittee: string;
  calYear: number;
  calMonth: number;
}
function _loadViewState(): Partial<MrViewState> {
  try {
    const raw = tlsGet(MR_VIEW_STATE_KEY);
    return raw ? (JSON.parse(raw) as Partial<MrViewState>) : {};
  } catch {
    return {};
  }
}
function _saveViewState(s: MrViewState): void {
  try {
    tlsSet(MR_VIEW_STATE_KEY, JSON.stringify(s));
  } catch {
    /* localStorage indisponible : on n'échoue pas l'affichage */
  }
}

// Pré-remplissage one-shot du formulaire de création de réunion (Dupliquer /
// Ajouter). Consommé puis effacé côté page-meeting.ts (même clé).
const MEETING_CREATE_PREFILL_KEY = 'meeting:createPrefill';
function _setCreatePrefill(pf: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(MEETING_CREATE_PREFILL_KEY, JSON.stringify(pf));
  } catch {
    /* sessionStorage indisponible : la création s'ouvrira vierge */
  }
}

export {};
